export const META = Symbol('RS_META');
export interface ProxyMeta {
  watchers: Set<() => void>;
}

export type ReactiveState<T extends object> = T & {
  [META]: ProxyMeta;
};

export type ProxiedReactiveState<T extends object> = T & {
  [META]: ProxyMeta;
};

export type AnyReactiveState = ReactiveState<object>;

export const isObject = (value: unknown): value is object =>
  typeof value === 'object' && value !== null;

const refSet = new WeakSet<object>();
export const canProxy = (x: unknown): x is object =>
  isObject(x) &&
  !refSet.has(x) &&
  (Array.isArray(x) || !(Symbol.iterator in x)) &&
  !(x instanceof Map) &&
  !(x instanceof Set) &&
  !(x instanceof WeakMap) &&
  !(x instanceof WeakSet) &&
  !(x instanceof Error) &&
  !(x instanceof Number) &&
  !(x instanceof Date) &&
  !(x instanceof String) &&
  !(x instanceof RegExp) &&
  !(x instanceof ArrayBuffer) &&
  !(x instanceof DataView) &&
  !(x instanceof Promise);

/** Mark an object to skip proxying (e.g. DOM nodes, editor state) */
export function Ref<T extends object>(value: T): T {
  refSet.add(value);
  return value;
}

export function getMeta(obj: ReactiveState<object>): ProxyMeta;
export function getMeta(obj: unknown): ProxyMeta | undefined;
export function getMeta(obj: unknown): ProxyMeta | undefined {
  if (!isObject(obj)) return undefined;
  return (obj as ReactiveState<object>)[META];
}

export function isReactiveState(obj: unknown): obj is ReactiveState<object> {
  return !!getMeta(obj);
}

export class MicrotaskBatcher {
  private pending = new Set<() => void>();
  private scheduled = false;

  scheduleCallbacks(cbs: Iterable<() => void> | (() => void) | undefined) {
    if (!cbs) return;
    if (typeof cbs === 'function') {
      this.pending.add(cbs);
    } else {
      for (const cb of cbs) this.pending.add(cb);
    }
    if (!this.scheduled) {
      this.scheduled = true;
      queueMicrotask(() => {
        this.scheduled = false;
        while (this.pending.size > 0) {
          const batch = [...this.pending];
          this.pending.clear();
          for (const fn of batch) fn();
        }
      });
    }
  }

  removeCallback(cb: () => void) {
    this.pending.delete(cb);
  }
}
